import Icon from "@Components/generic/icon";
import { faPause, faPlay, faVolumeHigh, faVolumeXmark } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Box, Button, CircularProgress, IconButton, Slider, Stack, Typography } from "@mui/material";
import { ActiveVideo } from "@Services/video-service/types";
import { useCdnPlayer } from "@Services/video-service/use-cdn-player";

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "0:00";

  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0")}`;
};

interface VideoControlsProps {
  player: ReturnType<typeof useCdnPlayer>;
}

const VideoControls = ({ player }: VideoControlsProps) => (
  <Stack
    spacing={0.5}
    sx={{
      position: "absolute",
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 3,
      px: 2.5,
      pb: 1.5,
      pt: 8,
      background: "linear-gradient(transparent, rgba(0, 0, 0, 0.8))",
    }}
  >
    <Slider
      aria-label="Video progress"
      min={0}
      max={player.duration || 1}
      value={Math.min(player.currentTime, player.duration || 1)}
      onChange={(_, value) => player.previewSeek(value as number)}
      onChangeCommitted={(_, value) => player.commitSeek(value as number)}
      sx={{
        color: "common.white",
        py: 0.5,
        "& .MuiSlider-rail": { opacity: 0.35 },
        "& .MuiSlider-thumb": { width: 12, height: 12 },
      }}
    />

    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton aria-label={player.isPlaying ? "Pause" : "Play"} onClick={player.togglePlayback} sx={{ color: "common.white" }}>
        <Icon icon={player.isPlaying ? faPause : faPlay} size="sm" />
      </IconButton>
      <IconButton aria-label={player.isMuted ? "Unmute" : "Mute"} onClick={player.toggleMuted} sx={{ color: "common.white" }}>
        <Icon icon={player.isMuted ? faVolumeXmark : faVolumeHigh} size="sm" />
      </IconButton>
      <Typography variant="textS500" color="common.white">
        {formatTime(player.currentTime)} / {formatTime(player.duration)}
      </Typography>
    </Stack>
  </Stack>
);

interface VideoDialogContentProps {
  video: ActiveVideo;
  onContinue: () => void;
}

export const VideoDialogContent = ({ video, onContinue }: VideoDialogContentProps) => {
  const { i18n } = useI18n();
  const player = useCdnPlayer();
  const title = video.title || i18n("career.watch.video");

  return (
    <Stack sx={{ height: "88vh" }}>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, bgcolor: "common.black" }}>
        <Box
          component="iframe"
          ref={player.iframeRef}
          onLoad={player.revealPlayer}
          src={`https://iframe.mediadelivery.net/embed/167314/${video.videoId}?autoplay=true&muted=false&playsinline=true&loop=false&preload=true`}
          title={title}
          allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen"
          allowFullScreen
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />

        {!player.isVisible && (
          <Box
            role="status"
            aria-label="Loading video"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 4,
              display: "grid",
              placeItems: "center",
              bgcolor: "common.black",
            }}
          >
            <CircularProgress size={48} thickness={3.5} sx={{ color: "common.white" }} />
          </Box>
        )}
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} sx={{ px: 3, py: 2, bgcolor: "dsSurface.v300" }}>
        <Typography variant="textLg600" color="dsBase.v200" noWrap>
          {title}
        </Typography>
        <Button size="large" onClick={onContinue} sx={{ minWidth: 180, borderRadius: 999 }}>
          {video.hasContinueAction ? i18n("challenges.play-challenge") : i18n("continue")}
        </Button>
      </Stack>
    </Stack>
  );
};
